
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { supabase, type SiteSettings } from "@/lib/supabase";

export const useSidebarData = () => {
  const { user, signOut } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [userName, setUserName] = useState<string>("Utente");
  const [clientId, setClientId] = useState<string | null>(null);
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  
  useEffect(() => {
    const checkUserRole = async () => {
      if (!user) return;
      
      try {
        // 1. Prima otteniamo il ruolo dell'utente dalla tabella profiles
        const { data, error } = await supabase
          .from('profiles')
          .select('role, email, avatar_url')
          .eq('id', user.id)
          .single();
        
        if (error) {
          console.error("Errore nel recupero del profilo:", error);
          setIsAdmin(false);
        } else {
          const isUserAdmin = data?.role === 'admin';
          setIsAdmin(isUserAdmin);
          
          if (data?.email) {
            const emailPrefix = data.email.split('@')[0];
            setUserName(emailPrefix || "Utente");
          }
          
          // Impostiamo l'avatar dalla tabella profiles
          let avatarUrl = data?.avatar_url;
          console.log("Avatar URL da profiles:", avatarUrl);
          
          // 2. Se l'utente non è un admin, potrebbe essere un cliente
          if (!isUserAdmin) {
            // Otteniamo l'ID cliente dalla tabella clients
            const { data: clientData, error: clientError } = await supabase
              .from('clients')
              .select('id, avatar_url, first_name, last_name')
              .eq('auth_user_id', user.id)
              .single();
              
            if (!clientError && clientData) {
              setClientId(clientData.id);
              
              // Utilizziamo il nome e cognome del cliente se disponibili
              if (clientData.first_name || clientData.last_name) {
                const clientName = `${clientData.first_name || ''} ${clientData.last_name || ''}`.trim();
                if (clientName) setUserName(clientName);
              }
              
              // Se il cliente ha un avatar, lo utilizziamo al posto di quello del profilo
              if (clientData.avatar_url) {
                console.log("Avatar URL trovato nel cliente:", clientData.avatar_url);
                avatarUrl = clientData.avatar_url;
              }
            }
          }
          
          // Impostiamo l'avatar URL finale
          setUserAvatar(avatarUrl);
          console.log("Avatar URL finale impostato:", avatarUrl);
        }
      } catch (error) {
        console.error("Errore nella verifica del ruolo:", error);
        setIsAdmin(false);
      }
    };
    
    checkUserRole();
  }, [user]);

  useEffect(() => {
    const fetchSiteSettings = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("*")
        .single();
      
      if (data) {
        setSiteSettings(data);
      }
    };
    
    fetchSiteSettings();
  }, []);

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      console.log("Tentativo di logout...");
      await signOut();
      console.log("Logout completato");
    } catch (error) {
      console.error("Errore durante il logout:", error);
      window.location.href = "/";
    }
  };

  return {
    user,
    isAdmin,
    userName,
    clientId,
    siteSettings,
    handleLogout,
    userAvatar
  };
};
